import React, { useEffect, useState } from 'react';
import { Brain, Loader2, Pin, PinOff, Sparkles, Trash2 } from 'lucide-react';

import { apiFetch } from '../../lib/apiBase';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';

const CATEGORY_STYLES = {
  marriage: 'border-rose-500/30 bg-rose-600/15 text-rose-300',
  parenting: 'border-blue-500/30 bg-blue-600/15 text-blue-300',
  career: 'border-amber-500/30 bg-amber-600/15 text-amber-300',
  faith: 'border-purple-500/30 bg-purple-600/15 text-purple-300',
  general: 'border-white/10 bg-white/5 text-gray-300',
};

const PeerSupportTab = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pinningId, setPinningId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await apiFetch('/posts?category=peer_support');
      if (!response.ok) {
        throw new Error(`Failed to fetch peer support posts (${response.status})`);
      }

      const data = await response.json();
      setPosts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch peer support posts:', error);
      toast.error('Failed to load peer support posts');
    } finally {
      setLoading(false);
    }
  };

  const handleRunAnalysis = async () => {
    setAnalyzing(true);
    try {
      toast.info('AI analysis for Peer Support is coming soon.');
    } finally {
      setAnalyzing(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleTogglePinned = async (post) => {
    setPinningId(post.post_id);
    try {
      const response = await apiFetch(`/posts/${post.post_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pinned: !post.is_pinned }),
      });

      if (!response.ok) {
        const responseBody = await response.json().catch(() => ({}));
        throw new Error(responseBody?.detail || `Failed to update pin (${response.status})`);
      }

      const updated = await response.json();
      setPosts((current) =>
        current
          .map((item) => (item.post_id === post.post_id ? updated : item))
          .sort((left, right) => Number(Boolean(right.is_pinned)) - Number(Boolean(left.is_pinned)))
      );
      toast.success(updated.is_pinned ? 'Post pinned' : 'Post unpinned');
    } catch (error) {
      console.error('Failed to update pin state:', error);
      toast.error(error.message || 'Failed to update pin state');
    } finally {
      setPinningId(null);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this peer support post?')) return;

    setDeletingId(postId);
    try {
      const response = await apiFetch(`/posts/${postId}`, { method: 'DELETE' });
      if (!response.ok) {
        const responseBody = await response.json().catch(() => ({}));
        throw new Error(responseBody?.detail || `Failed to delete post (${response.status})`);
      }

      setPosts((current) => current.filter((item) => item.post_id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      console.error('Failed to delete peer support post:', error);
      toast.error(error.message || 'Failed to delete post');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-white">Peer Support Questions</h3>
          <p className="mt-1 text-sm text-gray-400">
            Review posts, pin important questions, and remove content when needed.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-aqua" />
          </div>
        ) : posts.length === 0 ? (
          <p className="py-12 text-center text-gray-500">No peer support posts yet</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-[#2a2a32] hover:bg-transparent">
                <TableHead className="text-gray-400">Author</TableHead>
                <TableHead className="text-gray-400">Title</TableHead>
                <TableHead className="text-gray-400">Category</TableHead>
                <TableHead className="text-gray-400">Replies</TableHead>
                <TableHead className="text-gray-400">Posted At</TableHead>
                <TableHead className="text-gray-400">Pinned</TableHead>
                <TableHead className="text-right text-gray-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {posts.map((post) => {
                const category = post.focus_area || 'general';
                return (
                  <TableRow key={post.post_id} className="border-[#2a2a32] hover:bg-white/[0.02]">
                    <TableCell className="text-white">{post.author_name || 'A Dad in the Brotherhood'}</TableCell>
                    <TableCell className="max-w-[320px] text-white">
                      <div className="space-y-1">
                        <p className="font-medium">{post.title}</p>
                        <p className="line-clamp-2 text-sm text-gray-400">{post.content}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={CATEGORY_STYLES[category] || CATEGORY_STYLES.general}>
                        {category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-300">{post.replies?.length || 0}</TableCell>
                    <TableCell className="text-gray-300">
                      {post.created_at ? new Date(post.created_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>
                      {post.is_pinned ? (
                        <Badge className="border-aqua/30 bg-aqua/10 text-aqua">Pinned</Badge>
                      ) : (
                        <span className="text-gray-500">No</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="text-gray-300 hover:bg-white/10 hover:text-white"
                          disabled={pinningId === post.post_id}
                          onClick={() => handleTogglePinned(post)}
                        >
                          {pinningId === post.post_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : post.is_pinned ? (
                            <PinOff className="h-4 w-4" />
                          ) : (
                            <Pin className="h-4 w-4" />
                          )}
                          {post.is_pinned ? 'Unpin' : 'Pin'}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:bg-red-500/10 hover:text-red-300"
                          disabled={deletingId === post.post_id}
                          onClick={() => handleDelete(post.post_id)}
                        >
                          {deletingId === post.post_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Brain className="w-5 h-5 text-aqua" />
            AI Peer Support Analysis
          </h3>
          <Button
            onClick={handleRunAnalysis}
            disabled={analyzing}
            className="bg-aqua hover:bg-aqua-dark text-charcoal gap-2"
          >
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {analyzing ? 'Analyzing...' : 'Run Analysis'}
          </Button>
        </div>
        <p className="text-gray-500 text-center py-8">
          Click &quot;Run Analysis&quot; to get AI-powered insights from peer support questions and replies.
        </p>
      </Card>
    </div>
  );
};

export default PeerSupportTab;
